import { Cause, Effect, Exit, Queue, Scope } from "effect";
import type { QueryWork } from "../state/query-work.ts";
import { makeChatQueryScopes, type ChatQuery } from "./query-scopes.ts";
export type { ChatQuery } from "./query-scopes.ts";
/** The completion loop closes a query from outside its own running fiber. */
export const makeChatQueries = Effect.fn("Frontend.Chat.makeQueries")(function* (
  owner: Scope.Scope,
  queries: typeof QueryWork.Service,
  retain: (cause: Cause.Cause<never>) => Effect.Effect<void>
) {
  const scopes = yield* makeChatQueryScopes(owner, exit => Exit.isFailure(exit) ? retain(exit.cause) : Effect.void);
  const completions = yield* Queue.unbounded<Effect.Effect<void>>();
  yield* Effect.addFinalizer(() => Queue.shutdown(completions));
  yield* Effect.forkIn(Effect.forever(Queue.take(completions).pipe(Effect.flatMap(completion => completion.pipe(
    Effect.catchCause(cause => retain(cause))
  )))), owner);
  const start = <A>(operation: Effect.Effect<A>, complete: (value: A, query: ChatQuery) => Effect.Effect<void>, admit: (query: ChatQuery) => Effect.Effect<boolean> = () => Effect.succeed(true)): Effect.Effect<ChatQuery> => Effect.uninterruptible(Effect.gen(function* () {
    const query = yield* scopes.open;
    if (!(yield* admit(query))) { yield* query.close; return yield* Effect.interrupt; }
    const task = queries.observe(operation.pipe(Effect.onExit(exit => Exit.isFailure(exit) ? retain(exit.cause) : Effect.void))).pipe(
      Effect.flatMap(value => Effect.sync(() => { Queue.offerUnsafe(completions, complete(value, query).pipe(Effect.ensuring(query.close))); })),
      Effect.onExit(exit => Exit.isFailure(exit) ? retain(exit.cause) : Effect.void)
    );
    yield* Effect.forkIn(task, query.scope, { uninterruptible: false });
    return query;
  }));
  return { start };
});
